import { Injectable, inject } from '@angular/core';
import {
  Firestore,
  collection,
  query,
  where,
  getDocs,
  getDoc,
  addDoc,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  increment,
  runTransaction,
  DocumentReference
} from '@angular/fire/firestore';
import { Observable, from } from 'rxjs';
import { Song } from '../entities/song.model';

@Injectable({
  providedIn: 'root'
})
export class SongService {
  private firestore: Firestore;

  constructor() {
    this.firestore = inject(Firestore);
  }

  getSongsByTopic(language: string, topic: string): Observable<Song[]> {
    const songsCollection = collection(this.firestore, 'songs');
    const songsQuery = query(
      songsCollection,
      where('language', '==', language),
      where('topic', '==', topic)
    );
    return from(
      getDocs(songsQuery).then(snapshot =>
        snapshot.docs
          .map(d => ({ id: d.id, ...d.data() } as Song))
          .sort((a, b) => (b.upvotes - b.downvotes) - (a.upvotes - a.downvotes))
      )
    );
  }

  async getSong(songId: string): Promise<Song | null> {
    const snap = await getDoc(doc(this.firestore, `songs/${songId}`));
    return snap.exists() ? ({ id: snap.id, ...snap.data() } as Song) : null;
  }

  addSong(song: Partial<Song>): Promise<DocumentReference> {
    const songsCollection = collection(this.firestore, 'songs');
    return addDoc(songsCollection, song);
  }

  voteSong(songId: string, field: 'upvotes' | 'downvotes'): Promise<void> {
    const songDoc = doc(this.firestore, `songs/${songId}`);
    return updateDoc(songDoc, { [field]: increment(1) });
  }

  switchVote(songId: string, from: 'upvotes' | 'downvotes', to: 'upvotes' | 'downvotes'): Promise<void> {
    const songDoc = doc(this.firestore, `songs/${songId}`);
    return updateDoc(songDoc, { [from]: increment(-1), [to]: increment(1) });
  }

  async getUserVotes(uid: string, songIds: string[]): Promise<Record<string, 'up' | 'down'>> {
    const result: Record<string, 'up' | 'down'> = {};
    if (!songIds.length) return result;
    // Firestore 'in' queries support up to 30 values — batch if needed
    for (let i = 0; i < songIds.length; i += 30) {
      const chunk = songIds.slice(i, i + 30);
      const snap = await getDocs(query(
        collection(this.firestore, 'song_votes'),
        where('uid', '==', uid),
        where('songId', 'in', chunk)
      ));
      for (const d of snap.docs) {
        const data = d.data() as { vote: 'up' | 'down'; songId: string };
        result[data.songId] = data.vote;
      }
    }
    return result;
  }

  async setUserVote(uid: string, songId: string, vote: 'up' | 'down'): Promise<void> {
    const ref = doc(this.firestore, `song_votes/${uid}_${songId}`);
    await setDoc(ref, { uid, songId, vote });
  }

  async incrementCommentCount(songId: string): Promise<void> {
    const songDoc = doc(this.firestore, `songs/${songId}`);
    await updateDoc(songDoc, { commentCount: increment(1) });
  }

  async decrementCommentCount(songId: string): Promise<void> {
    const songDoc = doc(this.firestore, `songs/${songId}`);
    await runTransaction(this.firestore, async (tx) => {
      const snap = await tx.get(songDoc);
      const current = (snap.data()?.['commentCount'] ?? 0) as number;
      if (current > 0) {
        tx.update(songDoc, { commentCount: increment(-1) });
      }
    });
  }

  async updateSong(
    songId: string,
    updates: Partial<Pick<Song, 'title' | 'note' | 'youtubeUrl' | 'channelName' | 'thumbnailUrl'>>
  ): Promise<void> {
    const songDoc = doc(this.firestore, `songs/${songId}`);
    await updateDoc(songDoc, updates as any);
  }

  /** Persists pre-fetched transcript segments (e.g. from lrclib) to Firestore. */
  async saveSegments(songId: string, segments: { text: string; start: number; duration: number }[]): Promise<void> {
    const songDoc = doc(this.firestore, `songs/${songId}`);
    await updateDoc(songDoc, { transcriptSegments: segments });
  }

  async saveTranslations(songId: string, langKey: string, translations: string[]): Promise<void> {
    const songDoc = doc(this.firestore, `songs/${songId}`);
    await updateDoc(songDoc, { [`transcriptTranslations.${langKey}`]: translations });
  }

  async deleteSong(songId: string): Promise<void> {
    const songDoc = doc(this.firestore, `songs/${songId}`);
    await deleteDoc(songDoc);
  }

  async isDuplicateUrl(language: string, topic: string, youtubeUrl: string): Promise<boolean> {
    const videoId = this.extractVideoId(youtubeUrl);
    if (!videoId) return false;
    const snap = await getDocs(query(
      collection(this.firestore, 'songs'),
      where('language', '==', language),
      where('topic', '==', topic)
    ));
    return snap.docs.some(d => this.extractVideoId((d.data() as any).youtubeUrl || '') === videoId);
  }

  private extractVideoId(url: string): string {
    const match = url.match(/(?:v=|youtu\.be\/|embed\/|shorts\/)([a-zA-Z0-9_-]{11})/);
    return match ? match[1] : '';
  }
}
